import type React from "react";
import { useState } from "react";
import {
  Box,
  Grid,
  CircularProgress,
  Typography,
  Snackbar,
  Alert,
  Paper,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import type { RootState } from "../../../store/Index";
import { useStreamData } from "../hooks/useStreamData";
import { BaseURL } from "../../../service/Api";

import FundraiserCard from "../components/FundraiserCard";
import StreamFeedbackSummaryCard from "../components/StreamFeedbackSummaryCard";
import StreamHeroV2 from "./StreamHeroV2";
import SiteVisitBanner from "./SiteVisitBanner";
import StreamSidebarV2 from "./StreamSidebarV2";
import OverviewTab from "./tabs/OverviewTab";
import ScheduleTab from "./tabs/ScheduleTab";
import MediaTab from "./tabs/MediaTab";
import ActivityTab from "./tabs/ActivityTab";
import NotesTab from "./tabs/NotesTab";
import MemoryBookTab from "./tabs/MemoryBookTab";

import YouTubeModal from "../components/youtube-modal/YoutubeModal";
import AddNetworkingStatisticsModal from "../components/Networking";
import ThumbnailPreviewDialog from "../components/thumbnail/Thumbnail";
import SelectYouTubeChannelModal from "../components/youtube-modal/SelectYoutubeChannel";
import SelectTemplateModal from "../components/share-message/SelectTemplateModal";
import SocialShareModal from "../components/share-message/ShareMessage";
import TemplateSelectorDialog from "../components/memorial-page/components/shared-ui/TemplateSelectorDialog";

import type { ShareTemplate } from "../../configurations/share-messages/types/share-template.types";
import { formatDateToLongString } from "../../../utils/FormatDate";

import muzukuruChannellogo from "./../../../assets/Muzukuruimg3.png";
import livestreamingChannellogo from "./../../../assets/livestreaminglogo.png";

const TABS = [
  { label: "Overview", value: "overview" },
  { label: "Time Allocation", value: "schedule" },
  { label: "Graphics and Media", value: "media" },
  { label: "Networking", value: "network-stats" },
  { label: "Notes", value: "notes" },
  { label: "Memory Book", value: "memory-book" },
];

const channels = [
  {
    id: "1",
    name: "Muzukuru Funeral Streaming Services",
    logo: muzukuruChannellogo,
    email: "@Muzukuru",
  },
  {
    id: "2",
    name: "The Livestreaming Company",
    logo: livestreamingChannellogo,
    email: "@The Livestreaming Company",
  },
];

const clean = (val: string | undefined | null): string => {
  if (val === null || val === undefined || val === "N/A") return "";
  return val;
};

const ViewStreamV2: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const isStaging = BaseURL.includes("staging.muzukuru.com");

  const {
    stream,
    crewMembersData,
    isLoading,
    error,
    timeData,
    networkData,
    fetchData,
    fetchNetworkData,
  } = useStreamData(id);

  const [activeTab, setActiveTab] = useState("overview");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [socialModalOpen, setSocialModalOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<
    "facebook" | "whatsapp"
  >("facebook");
  const [thumbnailModalOpen, setThumbnailModalOpen] = useState(false);
  const [thumbnailType, setThumbnailType] = useState<string | null>(null);
  const [isYouTubeModalOpen, setIsYouTubeModalOpen] = useState(false);
  const [channelSelectOpen, setChannelSelectOpen] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState<{
    name?: string;
  } | null>(null);
  const [selectTemplateOpen, setSelectTemplateOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] =
    useState<ShareTemplate | null>(null);
  const [downloadFn, setDownloadFn] = useState<null | (() => void)>(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success",
  );

  const [thumbnailRefreshTrigger, setThumbnailRefreshTrigger] = useState(0);

  const streamId = id ? Number.parseInt(id) : undefined;

  // Handlers
  const handleOpenThumbnailModal = () => {
    setThumbnailType("youtube_thumbnail");
    setThumbnailModalOpen(true);
  };

  // const handleOpenFabThumbnailModal = () => {
  //   setThumbnailType("aone");
  //   setThumbnailModalOpen(true);
  // };

  const handleSocialShare = (platform: "facebook" | "whatsapp") => {
    setSelectedPlatform(platform);
    setSelectTemplateOpen(true);
  };

  const handleSelectTemplate = (template: ShareTemplate) => {
    setSelectedTemplate(template);
    setSocialModalOpen(true);
  };

  const handleSaveNetworkingData = (_data: unknown[]) => {
    void _data;
    setSnackbarMessage("Networking statistics saved.");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  const handleThumbnailSaved = (_url: string) => {
    void _url;
    setTimeout(() => {
      setThumbnailRefreshTrigger((prev) => prev + 1);
    }, 1500);
  };

  const handleYouTubeLinkSuccess = () => {
    fetchData();
  };

  const handleCloseSnackbar = (
    _event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const createSlug = (text: string) =>
    text
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");

  const buildMemorialUrl = () => {
    if (!stream) return "/";
    const bookingId = stream.booking.id;
    const serviceType = stream.booking?.service;
    let slugSource = "";
    let pageType = "";

    if (serviceType === "Funeral" || serviceType === "FuneralHome") {
      const deceased = stream.booking?.deceased?.[0];
      slugSource = [
        deceased?.first_name,
        deceased?.second_name,
        deceased?.surname,
      ]
        .filter(Boolean)
        .join(" ");
      pageType = "memorial-page";
    } else if (serviceType === "Church") {
      slugSource = stream.title || "church-service";
      pageType = "church-page";
    } else if (serviceType === "Conference") {
      slugSource = stream.title || "conference";
      pageType = "conference-page";
    } else {
      slugSource = "service";
      pageType = "service-page";
    }

    return `/${pageType}/${bookingId}/${encodeURIComponent(createSlug(slugSource))}`;
  };

  const handleNavigateToMemorial = () => {
    if (!stream) return;
    const serviceType = stream.booking?.service;
    if (!isStaging || serviceType === "Funeral_Home") {
      navigate(buildMemorialUrl());
      return;
    }
    if (stream.template_id) {
      navigate(buildMemorialUrl());
    } else {
      setTemplateDialogOpen(true);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !stream) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="error">{error || "Stream not found"}</Typography>
      </Box>
    );
  }

  const deceasedName = [
    clean(stream.booking.deceased[0]?.first_name),
    clean(stream.booking.deceased[0]?.second_name),
    clean(stream.booking.deceased[0]?.surname),
  ]
    .filter(Boolean)
    .join(" ");

  const completionChecks = [
    !!stream.youtube_stream_link,
    !!stream.website_stream_link,
    crewMembersData.length > 0,
    !!stream.address_one,
    timeData.shoot_start_time !== "N/A",
  ];
  const completionScore = Math.round(
    (completionChecks.filter(Boolean).length / completionChecks.length) * 100,
  );

  const serviceType = stream.booking?.service ?? "";

  const streamData = {
    deceasedName,
    date: formatDateToLongString(stream.date),
    time: stream.start,
    serviceType,
    eventName: stream?.booking?.events?.[0]?.title || "",
    venueName:
      stream?.service?.venue?.name &&
      stream?.booking?.events?.[0]?.venue?.name !== "N/A"
        ? stream?.service?.venue?.name
        : "",
    address: [
      stream.address_one,
      stream.address_two,
      stream.suburb,
      stream.city,
    ]
      .map(clean)
      .filter(Boolean)
      .join(", "),
    website_stream_link: stream.website_stream_link || "",
    imageUrl: "",
  };

  return (
    <Box>
      <SiteVisitBanner
        venueId={
          stream.service?.venue?.id ||
          stream.booking?.events?.[0]?.venue?.id ||
          stream.event_details?.venue?.id ||
          undefined
        }
        venue={{
          id:
            stream.service?.venue?.id ||
            stream.booking?.events?.[0]?.venue?.id ||
            stream.event_details?.venue?.id ||
            0,
          name:
            stream.service?.venue?.name ||
            stream.booking?.events?.[0]?.venue?.name ||
            stream.event_details?.venue?.name ||
            "",
          address_one:
            stream.service?.venue?.address_one ||
            stream.booking?.events?.[0]?.venue?.address_one ||
            "",
          city:
            stream.service?.venue?.city ||
            stream.booking?.events?.[0]?.venue?.city ||
            "",
        }}
      />
      <Grid container spacing={2} alignItems="flex-start">
        <Grid size={{ xs: 12, lg: 8 }}>
          <Paper sx={{ p: 2, mb: 2, borderRadius: "20px", boxShadow: 0 }}>
            <StreamHeroV2
              stream={stream}
              completionScore={completionScore}
              deceasedName={deceasedName}
            />
          </Paper>

          <Paper sx={{ borderRadius: "20px", boxShadow: 0 }}>
            <Box
              sx={{
                display: "flex",
                overflowX: "auto",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              {TABS.map((tab) => (
                <Box
                  key={tab.value}
                  onClick={() => setActiveTab(tab.value)}
                  sx={{
                    px: 2,
                    py: 1.5,
                    cursor: "pointer",
                    width: "100%",
                    whiteSpace: "nowrap",
                    fontSize: "1rem",
                    fontWeight: activeTab === tab.value ? 700 : 400,
                    color:
                      activeTab === tab.value
                        ? "primary.main"
                        : "text.secondary",
                    borderBottom:
                      activeTab === tab.value
                        ? "2px solid"
                        : "2px solid transparent",
                    borderColor:
                      activeTab === tab.value ? "primary.main" : "transparent",
                    transition: "color 0.15s ease, border-color 0.15s ease",
                    "&:hover": { color: "primary.main" },
                  }}
                >
                  {tab.label}
                </Box>
              ))}
            </Box>

            {/* Tab content */}
            <Box sx={{ p: 2 }}>
              {activeTab === "overview" && (
                <OverviewTab
                  stream={stream}
                  streamId={streamId}
                  refreshTrigger={thumbnailRefreshTrigger}
                  onReadyDownload={(fn) => setDownloadFn(() => fn)}
                />
              )}
              {activeTab === "schedule" && <ScheduleTab timeData={timeData} />}

              {activeTab === "media" && (
                <MediaTab
                  stream={stream}
                  streamId={streamId}
                  deceasedName={deceasedName}
                  refreshTrigger={thumbnailRefreshTrigger}
                  downloadFn={downloadFn}
                  onReadyDownload={(fn) => setDownloadFn(() => fn)}
                />
              )}
              {activeTab === "network-stats" && (
                <ActivityTab
                  networkData={networkData}
                  onOpenNetworking={() => setIsModalOpen(true)}
                />
              )}
              {activeTab === "notes" && <NotesTab streamId={streamId} />}
              {activeTab === "memory-book" && <MemoryBookTab stream={stream} />}
            </Box>
          </Paper>
        </Grid>

        {/* Right sidebar */}
        <Grid size={{ xs: 12, lg: 4 }}>
          {streamId && (
            <Box sx={{ mb: 2 }}>
              <FundraiserCard streamId={streamId} />
            </Box>
          )}
          {streamId && (
            <Box sx={{ mb: 2 }}>
              <StreamFeedbackSummaryCard streamId={streamId} />
            </Box>
          )}
          <StreamSidebarV2
            stream={stream}
            streamId={streamId}
            crewMembersData={crewMembersData}
            onCreateThumbnail={handleOpenThumbnailModal}
            onCreateYouTubeLink={() => setChannelSelectOpen(true)}
            onSocialShare={handleSocialShare}
            onNavigateToMemorial={handleNavigateToMemorial}
          />
        </Grid>
      </Grid>

      {/* {serviceType !== "Church" && serviceType !== "Conference" && (
        <Fab
          variant="extended"
          color="primary"
          onClick={handleOpenFabThumbnailModal}
          sx={{ position: "fixed", bottom: 16, right: 16 }}
        >
          Download A1
        </Fab>
      )} */}

      <YouTubeModal
        open={isYouTubeModalOpen}
        onClose={() => setIsYouTubeModalOpen(false)}
        streamData={stream}
        crewMembers={crewMembersData}
        onSuccess={handleYouTubeLinkSuccess}
        selectedChannel={selectedChannel}
      />

      <SelectYouTubeChannelModal
        open={channelSelectOpen}
        onClose={() => setChannelSelectOpen(false)}
        channels={channels}
        onSelect={(channel) => {
          setSelectedChannel(channel);
          setChannelSelectOpen(false);
          setIsYouTubeModalOpen(true);
        }}
      />

      <AddNetworkingStatisticsModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveNetworkingData}
        streamId={Number(id)}
        refreshNetworkData={fetchNetworkData}
      />

      <SocialShareModal
        open={socialModalOpen}
        onClose={() => setSocialModalOpen(false)}
        streamData={streamData}
        platform={selectedPlatform}
        selectedTemplate={selectedTemplate}
      />

      <SelectTemplateModal
        open={selectTemplateOpen}
        onClose={() => setSelectTemplateOpen(false)}
        onSelectTemplate={handleSelectTemplate}
        serviceType={stream.booking?.service}
      />

      <ThumbnailPreviewDialog
        open={thumbnailModalOpen}
        onClose={() => setThumbnailModalOpen(false)}
        thumbnailType={thumbnailType}
        streamData={{
          id: stream.id?.toString() || "0",
          booking_id: stream.booking?.id?.toString() || "0",
          bookingData: {
            booking_id: stream.booking?.id?.toString() || "0",
            booking_type: stream.booking?.booking_type as
              | "FUNERAL"
              | "CHURCH"
              | "FUNERAL_HOME",
            deceased_name: stream.booking?.deceased?.[0]?.first_name || "",
            deceased_surname: stream.booking?.deceased?.[0]?.surname || "",
            deceased_second_name:
              stream.booking?.deceased?.[0]?.second_name || "",
            deceased_dob: stream.booking?.deceased?.[0]?.date_of_birth || "",
            deceased_dod: stream.booking?.deceased?.[0]?.date_of_death || "",
            deceased_photo_url:
              stream.booking?.deceased?.[0]?.person_image || "",
            deceased_picture_url:
              stream.booking?.deceased?.[0]?.person_image || "",
            deceased_ids: stream.booking?.deceased?.map((d) => d.id) || [],
          },
          booking: stream.booking
            ? {
                deceased: stream.booking.deceased?.map((d) => ({
                  id: d.id,
                  first_name: d.first_name,
                  second_name: d.second_name,
                  surname: d.surname,
                  date_of_birth: d.date_of_birth,
                  date_of_death: d.date_of_death,
                  gender: d.gender,
                  person_image: d.person_image || "",
                })),
              }
            : undefined,
          venue_name:
            stream.booking?.events?.[0]?.venue?.name ||
            stream.booking?.services?.[0]?.venue?.name ||
            "",
          start_time: stream.start,
          end_time: stream.end,
          special_notes: stream.service?.service_notes,
          website_stream_link: stream.website_stream_link || "",
          date: stream.date || "",
          location:
            stream.service?.venue?.name ||
            stream.booking?.services?.[0]?.venue?.name ||
            "N/A",
          address: `${stream.address_one || ""}, ${stream.city || ""}`,
          shoot_from: stream.start || "",
          title: stream.title || "",
        }}
        onSave={handleThumbnailSaved}
        onDownload={() => {
          console.log("Thumbnail downloaded successfully");
        }}
        refreshThumbnails={() => {
          console.log("Refreshing thumbnails");
        }}
        refreshStream={fetchData}
      />

      <TemplateSelectorDialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        serviceType={stream.booking?.service ?? "Funeral"}
        streamId={stream.id}
        currentTemplateId={stream.template_id}
        onConfirm={() => navigate(buildMemorialUrl())}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          severity={snackbarSeverity}
          sx={{ width: "100%", color: reduxTheme.primary_colors[900] }}
          onClose={handleCloseSnackbar}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ViewStreamV2;
